import { useEffect, useState } from "react";
import { TaskDisclosure } from "../../components/TaskDisclosure";

function formatMoney(amountCents = 0, currency = "usd") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency
  }).format(Number(amountCents || 0) / 100);
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString() : "Not recorded";
}

function filterAmountValue(value) {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  const dollars = Number(value) / 100;
  return Number.isFinite(dollars) ? String(dollars) : "";
}

function nextAmountFilter(value) {
  if (value === "") {
    return "";
  }
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) && amount >= 0 ? String(Math.round(amount * 100)) : "";
}

function donationStatusLabel(value) {
  const labels = {
    paid: "Received",
    checkout_started: "Public page started",
    pending: "Pending",
    failed: "Failed",
    cancelled: "Cancelled",
    refunded: "Refunded"
  };
  return labels[value] || "Needs review";
}

function donationSourceLabel(value) {
  const labels = {
    public_donation_form: "Public page",
    owner_manual: "Manual entry"
  };
  return labels[value] || "Donation record";
}

function hasActiveFilters(filters = {}) {
  return Object.values(filters).some((value) => value !== undefined && value !== null && value !== "");
}

export function DonationsPage({
  donationForm,
  donationFilters,
  donationSettingsForm,
  donations = [],
  loading,
  onExportDonations,
  onDownloadDonationReceipt,
  onCreateDonation,
  onRecordDonationRefund,
  onSaveDonationSettings,
  exportingDonations,
  loadingDonationReceiptId,
  recordingDonationRefundId,
  savingDonation,
  savingDonationSettings,
  selectedRange,
  setDonationForm,
  setDonationFilters,
  setDonationSettingsForm,
  summary = {}
}) {
  const [activeDonationSection, setActiveDonationSection] = useState(() => {
    if (typeof window === "undefined") {
      return "settings";
    }
    const hash = window.location.hash.replace("#donations-", "");
    return ["settings", "record", "history"].includes(hash) ? hash : "settings";
  });
  useEffect(() => {
    function syncDonationSectionFromHash() {
      const hash = window.location.hash.replace("#donations-", "");
      if (["settings", "record", "history"].includes(hash)) {
        setActiveDonationSection(hash);
      }
    }

    syncDonationSectionFromHash();
    window.addEventListener("hashchange", syncDonationSectionFromHash);
    return () => window.removeEventListener("hashchange", syncDonationSectionFromHash);
  }, []);

  if (!selectedRange) {
    return null;
  }

  const donationSummary = summary || {};
  const donationSections = [
    { id: "settings", label: "Public page", meta: donationSettingsForm.publicDonationsEnabled ? "Visible" : "Hidden" },
    { id: "record", label: "Record gift", meta: "Cash or check" },
    { id: "history", label: "Gift history", meta: `${donations.length} records` }
  ];

  function switchDonationSection(sectionId) {
    setActiveDonationSection(sectionId);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#donations-${sectionId}`);
    }
  }

  return (
    <section className="panel module-panel" id="donations">
      {loading && <div className="empty-state">Loading donations...</div>}

      <div className="resource-summary-grid">
        <div>
          <span>Gift records</span>
          <strong>{donationSummary.returned || donations.length || 0}</strong>
        </div>
        <div>
          <span>Received</span>
          <strong>{formatMoney(donationSummary.paidTotalCents || 0)}</strong>
        </div>
        <div>
          <span>Needs follow-up</span>
          <strong>{donations.filter((donation) => ["pending", "checkout_started"].includes(donation.status)).length}</strong>
        </div>
        <div className="summary-action-cell">
          <span>Gift ledger</span>
          <button type="button" className="secondary-button" disabled={exportingDonations || donations.length === 0} onClick={onExportDonations}>
            {exportingDonations ? "Exporting..." : "Export gift records"}
          </button>
        </div>
      </div>

      <nav className="console-section-nav" aria-label="Donations sections">
        {donationSections.map((section) => (
          <button
            type="button"
            className={activeDonationSection === section.id ? "is-active" : ""}
            key={section.id}
            onClick={() => switchDonationSection(section.id)}
          >
            <span>{section.label}</span>
            <small>{section.meta}</small>
          </button>
        ))}
      </nav>

      {activeDonationSection === "settings" && (
      <form className="membership-settings-panel" onSubmit={onSaveDonationSettings}>
        <div className="panel-header">
          <h3>Public donation page</h3>
          <p>Control how donation options appear on the public range page.</p>
        </div>
        <div className="form-grid">
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={donationSettingsForm.publicDonationsEnabled}
              onChange={(event) => setDonationSettingsForm((current) => ({ ...current, publicDonationsEnabled: event.target.checked }))}
            />
            Show donations on public page
          </label>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={donationSettingsForm.customAmountEnabled}
              onChange={(event) => setDonationSettingsForm((current) => ({ ...current, customAmountEnabled: event.target.checked }))}
            />
            Allow custom amount
          </label>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={donationSettingsForm.donorNotesEnabled}
              onChange={(event) => setDonationSettingsForm((current) => ({ ...current, donorNotesEnabled: event.target.checked }))}
            />
            Allow donor notes
          </label>
          <label>
            Currency
            <input
              value={donationSettingsForm.currency}
              onChange={(event) => setDonationSettingsForm((current) => ({ ...current, currency: event.target.value }))}
              placeholder="USD"
            />
          </label>
          <label>
            Minimum amount
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={donationSettingsForm.minimumAmount}
              onChange={(event) => setDonationSettingsForm((current) => ({ ...current, minimumAmount: event.target.value }))}
              placeholder="1.00"
            />
          </label>
          <label>
            Suggested amounts
            <input
              value={donationSettingsForm.suggestedAmounts}
              onChange={(event) => setDonationSettingsForm((current) => ({ ...current, suggestedAmounts: event.target.value }))}
              placeholder="25, 50, 100, 250"
            />
          </label>
        </div>
        <button type="submit" disabled={savingDonationSettings}>
          {savingDonationSettings ? "Saving..." : "Save public page"}
        </button>
      </form>
      )}

      {activeDonationSection === "record" && (
      <TaskDisclosure
        actionLabel="Record"
        title="Record a gift"
        description="Open this for cash, check, or other gifts received away from the public page."
      >
      <form className="membership-settings-panel" onSubmit={onCreateDonation}>
        <div className="panel-header">
          <h3>Record gift received</h3>
          <p>Use this for cash, check, or other gifts received away from the public page.</p>
        </div>
        <div className="form-grid">
          <label>
            Donor name
            <input
              value={donationForm.donorName}
              onChange={(event) => setDonationForm((current) => ({ ...current, donorName: event.target.value }))}
              placeholder="Optional"
            />
          </label>
          <label>
            Donor email
            <input
              type="email"
              value={donationForm.donorEmail}
              onChange={(event) => setDonationForm((current) => ({ ...current, donorEmail: event.target.value }))}
              placeholder="Optional"
            />
          </label>
          <label>
            Amount
            <input
              type="number"
              min="0"
              step="0.01"
              value={donationForm.amount}
              onChange={(event) => setDonationForm((current) => ({ ...current, amount: event.target.value }))}
              placeholder="50.00"
            />
          </label>
          <label>
            Status
            <select
              value={donationForm.status}
              onChange={(event) => setDonationForm((current) => ({ ...current, status: event.target.value }))}
            >
              <option value="paid">Received</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
        </div>
        <label>
          Note
          <textarea
            value={donationForm.note}
            onChange={(event) => setDonationForm((current) => ({ ...current, note: event.target.value }))}
            placeholder="Optional donor note or internal receipt detail."
          />
        </label>
        <button type="submit" disabled={savingDonation || Number.parseFloat(donationForm.amount || "0") <= 0}>
          {savingDonation ? "Recording..." : "Record gift"}
        </button>
      </form>
      </TaskDisclosure>
      )}

      {activeDonationSection === "history" && (
      <div className="membership-type-management">
        <div className="panel-header">
          <h3>Gift history</h3>
          <p>Public page and manually entered gifts are shown together.</p>
        </div>
        <div className="form-grid compact-form-grid">
          <label>
            Status
            <select
              value={donationFilters.status}
              onChange={(event) => setDonationFilters((current) => ({ ...current, status: event.target.value }))}
            >
              <option value="">All statuses</option>
              <option value="paid">Received</option>
              <option value="checkout_started">Public page started</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
            </select>
          </label>
          <label>
            Source
            <select
              value={donationFilters.source}
              onChange={(event) => setDonationFilters((current) => ({ ...current, source: event.target.value }))}
            >
              <option value="">All sources</option>
              <option value="public_donation_form">Public page</option>
              <option value="owner_manual">Manual entry</option>
            </select>
          </label>
          <label>
            Search
            <input
              value={donationFilters.q}
              onChange={(event) => setDonationFilters((current) => ({ ...current, q: event.target.value }))}
              placeholder="Donor, note, receipt status"
            />
          </label>
          <label>
            Donor email
            <input
              type="email"
              value={donationFilters.donorEmail}
              onChange={(event) => setDonationFilters((current) => ({ ...current, donorEmail: event.target.value }))}
              placeholder="member@example.com"
            />
          </label>
          <label>
            Recorded from
            <input
              type="date"
              value={donationFilters.createdFrom}
              onChange={(event) => setDonationFilters((current) => ({ ...current, createdFrom: event.target.value }))}
            />
          </label>
          <label>
            Recorded to
            <input
              type="date"
              value={donationFilters.createdTo}
              onChange={(event) => setDonationFilters((current) => ({ ...current, createdTo: event.target.value }))}
            />
          </label>
          <label>
            Received from
            <input
              type="date"
              value={donationFilters.paidFrom}
              onChange={(event) => setDonationFilters((current) => ({ ...current, paidFrom: event.target.value }))}
            />
          </label>
          <label>
            Received to
            <input
              type="date"
              value={donationFilters.paidTo}
              onChange={(event) => setDonationFilters((current) => ({ ...current, paidTo: event.target.value }))}
            />
          </label>
          <label>
            Min amount
            <input
              type="number"
              min="0"
              step="0.01"
              value={filterAmountValue(donationFilters.minAmountCents)}
              onChange={(event) => setDonationFilters((current) => ({ ...current, minAmountCents: nextAmountFilter(event.target.value) }))}
              placeholder="25.00"
            />
          </label>
          <label>
            Max amount
            <input
              type="number"
              min="0"
              step="0.01"
              value={filterAmountValue(donationFilters.maxAmountCents)}
              onChange={(event) => setDonationFilters((current) => ({ ...current, maxAmountCents: nextAmountFilter(event.target.value) }))}
              placeholder="250.00"
            />
          </label>
          <button
            type="button"
            className="secondary-button"
            disabled={!hasActiveFilters(donationFilters)}
            onClick={() => setDonationFilters({
              status: "",
              source: "",
              q: "",
              donorEmail: "",
              createdFrom: "",
              createdTo: "",
              paidFrom: "",
              paidTo: "",
              minAmountCents: "",
              maxAmountCents: ""
            })}
          >
            Clear filters
          </button>
        </div>
        {donations.length === 0 ? (
          <div className="empty-state">No gifts have been recorded yet.</div>
        ) : (
          donations.map((donation) => (
            <div className="table-like-row" key={donation.donationId}>
              <div>
                <strong>{donation.donorName || donation.donorEmail || "Anonymous donor"}</strong>
                <span>{formatMoney(donation.amountCents, donation.currency)} / {donationStatusLabel(donation.status)} / {donationSourceLabel(donation.source)}</span>
                {donation.note && <span>{donation.note}</span>}
                {donation.checkoutSessionId && <span>Public page gift</span>}
                {donation.receiptId && <span>Receipt available</span>}
              </div>
              <div className="row-actions">
                <span>{formatDateTime(donation.paidAt || donation.updatedAt || donation.createdAt)}</span>
                {donation.receiptId && (
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={loadingDonationReceiptId === donation.donationId}
                    onClick={() => onDownloadDonationReceipt(donation)}
                  >
                    {loadingDonationReceiptId === donation.donationId ? "Loading receipt..." : "Download receipt"}
                  </button>
                )}
                {donation.status === "paid" && (
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={recordingDonationRefundId === donation.donationId}
                    onClick={() => onRecordDonationRefund(donation)}
                  >
                    {recordingDonationRefundId === donation.donationId ? "Recording refund..." : "Record refund"}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      )}
    </section>
  );
}
